import React, { useState, useCallback, useEffect } from 'react';
import { Screen, defaultUserData } from './types';
import {
  SplashScreen,
  HomeScreen,
  ConsentScreen,
  VerificationMethodScreen,
  AadhaarEKYCScreen,
  ApplicantDetailsScreen,
  FaceMatchScreen,
  PurposeOfVisitScreen,
  ApplicationTypeScreen,
  PermitValidityScreen,
  PaymentScreen,
  PaymentSuccessScreen,
  SuccessScreen,
  MyPermitsScreen,
  MapScreen,
  ViewPermitScreen
} from './screens';
import api from '../../services/api';
import './ILPApplication.css';

const ILPApplication = () => {
  // State management
  const [currentScreen, setCurrentScreen] = useState(Screen.Splash);
  const [userData, setUserData] = useState(defaultUserData);
  const [purpose, setPurpose] = useState('Vacation / Tourism');
  const [validity, setValidity] = useState('10 Days');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [members, setMembers] = useState([]);
  const [visitingDistricts, setVisitingDistricts] = useState([]);
  const [checkGates, setCheckGates] = useState([]);
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [showInvitedModal, setShowInvitedModal] = useState(false);
  const [showAdminOtpModal, setShowAdminOtpModal] = useState(false);
  const [adminOtpValue, setAdminOtpValue] = useState('');
  const [adminOtpError, setAdminOtpError] = useState('');

  // Navigation handler
  const navigateTo = useCallback((screen) => {
    setCurrentScreen(screen);
    // Scroll to top on screen change
    window.scrollTo(0, 0);
  }, []);

  // Extract user details from Digilocker response
  const extractUserDetails = (details) => {
    if (!details) return null;
    
    // Handle both flattened and nested user_details structure
    const userDetails = details.user_details || details;
    
    const name = userDetails.name || details.name || details.poi?.name || details.full_name || '';
    const dob = userDetails.dob || details.dob || details.poi?.dob || details.date_of_birth || '';
    const gender = userDetails.gender || details.gender || details.poi?.gender || '';
    const address = details.address || userDetails.address || details.poa?.full || 
      [details.poa?.house, details.poa?.street, details.poa?.loc, details.poa?.dist, details.poa?.state, details.poa?.pc]
        .filter(Boolean).join(', ') || '';
    const fatherName = details.care_of || userDetails.care_of || details.poa?.co || details.father_name || '';
    const uid = details.uid || userDetails.uid || details.uid_number || details.aadhaar_number || '';
    const mobile = userDetails.mobile || details.mobile || '';
    
    // Try multiple paths for photo - DigiLocker returns it in various formats
    let photoUrl = userDetails.photo || details.photo || details.poi?.photo || 
                   details.photo_link || details.image || userDetails.image || 
                   details.photograph || userDetails.photograph || '';
    
    // Format photo URL correctly
    const formatPhotoUrl = (photo) => {
      if (!photo) return null;
      if (typeof photo !== 'string') return null;
      if (photo.startsWith('data:')) return photo;
      if (photo.startsWith('http')) return photo;
      // Assume it's base64 if it's a long string
      if (photo.length > 100) return `data:image/jpeg;base64,${photo}`;
      return null;
    };

    return {
      name,
      aadhaar: uid ? `XXXX-XXXX-${uid.slice(-4)}` : 'XXXX-XXXX-XXXX',
      fatherName,
      dob,
      gender: gender === 'M' ? 'Male' : gender === 'F' ? 'Female' : gender,
      address,
      photoUrl: formatPhotoUrl(photoUrl),
      mobile: mobile ? `+91 ${mobile}` : ''
    };
  };

  // Handle DigiLocker callback on page load
  useEffect(() => {
    const handleDigilockerCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const callback = urlParams.get('callback');
      const verificationId = urlParams.get('verification_id');
      const referenceId = urlParams.get('reference_id');

      if (callback === 'digilocker' && verificationId) {
        setIsProcessingCallback(true);
        setCurrentScreen(Screen.AadhaarEKYC); // Show loading state

        try {
          // First check status
          const statusResponse = await api.get(`/api/kyc/digilocker/status/${verificationId}`, {
            params: referenceId ? { reference_id: referenceId } : {}
          });

          const status = statusResponse.data?.status;
          const isVerified = status === 'COMPLETED' || status === 'AUTHENTICATED';
          
          if (statusResponse.data?.success && isVerified) {
            // Fetch the document
            const docResponse = await api.post('/api/kyc/digilocker/document', {
              verificationId,
              referenceId,
              documentType: 'AADHAAR'
            });

            if (docResponse.data?.success) {
              const details = docResponse.data?.details;
              const extractedData = extractUserDetails(details);
              
              if (extractedData) {
                setUserData({
                  ...extractedData,
                  verificationId,
                  documentUrl: docResponse.data?.documentUrl
                });
                
                // Clean up URL params
                window.history.replaceState({}, document.title, window.location.pathname);
                
                // Navigate to applicant details
                setCurrentScreen(Screen.ApplicantDetails);
              } else {
                // If extraction failed, go to home
                window.history.replaceState({}, document.title, window.location.pathname);
                setCurrentScreen(Screen.Home);
              }
            } else {
              window.history.replaceState({}, document.title, window.location.pathname);
              setCurrentScreen(Screen.Home);
            }
          } else {
            // Verification not complete or failed
            window.history.replaceState({}, document.title, window.location.pathname);
            setCurrentScreen(Screen.Home);
          }
        } catch (error) {
          console.error('DigiLocker callback error:', error);
          window.history.replaceState({}, document.title, window.location.pathname);
          setCurrentScreen(Screen.Home);
        } finally {
          setIsProcessingCallback(false);
        }
      }
    };

    handleDigilockerCallback();
  }, []);

  // Handle invite deep-link on page load (e.g. /kyc-demo?invite=1&inviter=... )
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const callback = urlParams.get('callback');
    // Don't interfere with DigiLocker callback flow
    if (callback === 'digilocker') return;

    const inviteFlag = urlParams.get('invite');
    if (!inviteFlag) return;

    const inviter = urlParams.get('inviter') || 'Someone';
    const inviterId = urlParams.get('inviter_id') || '';
    const ref = urlParams.get('ref') || '';
    const invitee = urlParams.get('invitee') || 'Tourist';

    setInviteInfo({ inviter, inviterId, ref, invitee });
    setShowInvitedModal(true);
    setCurrentScreen(Screen.Consent);

    // Clean up URL params
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  // Calculate member count (self + members)
  const memberCount = members.length + 1;

  const openAdminAccessModal = () => {
    setAdminOtpValue('');
    setAdminOtpError('');
    setShowAdminOtpModal(true);
  };

  const closeAdminAccessModal = () => {
    setShowAdminOtpModal(false);
    setAdminOtpValue('');
    setAdminOtpError('');
  };

  const verifyAdminOtpAndNavigate = () => {
    if (adminOtpValue.trim() === '32!') {
      closeAdminAccessModal();
      window.location.href = '/ilp-admin';
      return;
    }
    setAdminOtpError('Invalid OTP. Please try again.');
  };

  // Render current screen
  const renderScreen = () => {
    // Show loading screen while processing DigiLocker callback
    if (isProcessingCallback) {
      return (
        <div className="ilp-screen">
          <div className="ilp-callback-loading">
            <div className="ilp-loading-spinner large">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                <circle cx="12" cy="12" r="10" strokeOpacity="0.2" />
                <path d="M12 2a10 10 0 0110 10" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
                </path>
              </svg>
            </div>
            <h2>Processing Verification</h2>
            <p>Fetching your details from DigiLocker...</p>
          </div>
        </div>
      );
    }

    switch (currentScreen) {
      case Screen.Splash:
        return <SplashScreen navigateTo={navigateTo} />;
      
      case Screen.Home:
        return <HomeScreen navigateTo={navigateTo} />;
      
      case Screen.Consent:
        return <ConsentScreen navigateTo={navigateTo} />;
      
      case Screen.VerificationMethod:
        return <VerificationMethodScreen navigateTo={navigateTo} />;
      
      case Screen.AadhaarEKYC:
        return <AadhaarEKYCScreen navigateTo={navigateTo} setUserData={setUserData} />;
      
      case Screen.AadhaarDocument:
        // For document scan, redirect to eKYC for now (can be expanded later)
        return <AadhaarEKYCScreen navigateTo={navigateTo} setUserData={setUserData} />;
      
      case Screen.ApplicantDetails:
        return <ApplicantDetailsScreen userData={userData} navigateTo={navigateTo} />;
      
      case Screen.FaceMatch:
        return <FaceMatchScreen userData={userData} navigateTo={navigateTo} />;
      
      case Screen.PurposeOfVisit:
        return <PurposeOfVisitScreen purpose={purpose} setPurpose={setPurpose} navigateTo={navigateTo} />;
      
      case Screen.ApplicationType:
        return <ApplicationTypeScreen navigateTo={navigateTo} setMembers={setMembers} />;
      
      case Screen.GroupApplication:
        // For simplicity, redirect to individual flow
        navigateTo(Screen.PermitValidity);
        return null;
      
      case Screen.PermitValidity:
        return (
          <PermitValidityScreen 
            validity={validity}
            setValidity={setValidity}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            visitingDistricts={visitingDistricts}
            setVisitingDistricts={setVisitingDistricts}
            checkGates={checkGates}
            setCheckGates={setCheckGates}
            navigateTo={navigateTo}
          />
        );
      
      case Screen.Payment:
        return (
          <PaymentScreen 
            userData={userData}
            validity={validity}
            startDate={startDate}
            endDate={endDate}
            memberCount={memberCount}
            navigateTo={navigateTo}
          />
        );
      
      case Screen.PaymentSuccess:
        return <PaymentSuccessScreen navigateTo={navigateTo} />;
      
      case Screen.Success:
        return (
          <SuccessScreen 
            userData={userData}
            validity={validity}
            startDate={startDate}
            endDate={endDate}
            navigateTo={navigateTo}
          />
        );
      
      case Screen.ViewPermit:
        return (
          <ViewPermitScreen 
            userData={userData}
            purpose={purpose}
            startDate={startDate}
            endDate={endDate}
            navigateTo={navigateTo}
          />
        );
      
      case Screen.MyPermits:
        return <MyPermitsScreen navigateTo={navigateTo} />;
      
      case Screen.Map:
        return <MapScreen navigateTo={navigateTo} />;
      
      default:
        return <HomeScreen navigateTo={navigateTo} />;
    }
  };

  return (
    <div className="ilp-application">
      <button
        type="button"
        className="ilp-admin-access-btn"
        onClick={openAdminAccessModal}
      >
        ILP Admin
      </button>

      <div className="ilp-container">
        {renderScreen()}
      </div>

      {showAdminOtpModal && (
        <div className="ilp-modal-overlay ilp-admin-otp-overlay" onClick={closeAdminAccessModal}>
          <div className="ilp-admin-otp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Admin Access</h3>
            <p>Enter OTP to continue to ILP Admin page.</p>
            <input
              type="text"
              value={adminOtpValue}
              onChange={(e) => {
                setAdminOtpValue(e.target.value);
                if (adminOtpError) setAdminOtpError('');
              }}
              placeholder="Enter OTP"
              autoFocus
            />
            {adminOtpError && <span className="ilp-admin-otp-error">{adminOtpError}</span>}
            <div className="ilp-admin-otp-actions">
              <button type="button" className="ilp-secondary-btn" onClick={closeAdminAccessModal}>
                Cancel
              </button>
              <button type="button" className="ilp-primary-btn" onClick={verifyAdminOtpAndNavigate}>
                Verify
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invited by modal */}
      {showInvitedModal && (
        <div className="ilp-modal-overlay ilp-invite-overlay" onClick={() => setShowInvitedModal(false)}>
          <div className="ilp-invited-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ilp-invite-modal-header">
              <div>
                <h3>You’ve been invited</h3>
                <p>
                  {inviteInfo?.inviter ? (
                    <>
                      Invite from <strong>{inviteInfo.inviter}</strong><br/>
                      {inviteInfo?.ref ? <> Ref: <strong>{inviteInfo.ref}</strong></> : null}
                    </>
                  ) : (
                    'Invite link detected'
                  )}
                </p>
              </div>
              <button className="ilp-close-btn" onClick={() => setShowInvitedModal(false)} aria-label="Close invited modal">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="ilp-invited-body">
              <div className="ilp-invited-pill">
                <span className="ilp-invited-pill-label">Invitee</span>
                <span className="ilp-invited-pill-value">{inviteInfo?.invitee || 'Tourist'}</span>
              </div>
              <p className="ilp-invited-text">
                You’ll be taken to the start of the ILP application flow now.
              </p>
            </div>

            <div className="ilp-invited-actions">
              <button
                className="ilp-primary-btn full"
                onClick={() => {
                  setShowInvitedModal(false);
                  setCurrentScreen(Screen.Consent);
                }}
              >
                Start Application
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="ilp-modal-overlay ilp-demo-lock-overlay">
        <div className="ilp-demo-lock-modal" role="alertdialog" aria-modal="true" aria-labelledby="ilp-demo-lock-title">
          <h3 id="ilp-demo-lock-title">ILP Demo</h3>
          <p>Please contact Stoic Studios to enable this demo.</p>
        </div>
      </div>
    </div>
  );
};

export default ILPApplication;

